import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { X, CaretLeft, CaretRight, Funnel, FileXls, FilePdf } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { getDateFilter } from "@/lib/fyRange";
import { useDateFilterVersion } from "@/lib/useDateFilterVersion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtINR = (n) => {
  if (!n || Number(n) === 0) return "";
  return Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtDate = (iso) => {
  try {
    const d = new Date(iso + "T00:00:00");
    return `${d.getDate()}-${d.toLocaleString("en-GB", { month: "short" })}-${d.getFullYear()}`;
  } catch { return iso; }
};

const shiftDay = (iso, delta) => {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + delta);
  return d.toISOString().slice(0, 10);
};

export default function DayBookView({ onClose }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [date, setDate] = useState("");
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ debit: 0, credit: 0 });
  const [summary, setSummary] = useState(null);
  const [range, setRange] = useState({ from: "", to: "", mode: "day" });
  const [selected, setSelected] = useState(0);
  // filters
  const [showFilter, setShowFilter] = useState(false);
  // Global F2 date filter: single date -> open that day; range -> full-range mode
  const _df = getDateFilter();
  const [viewMode, setViewMode] = useState(_df ? (_df.from === _df.to ? "day" : "year") : "day"); // day | month | year
  const [month, setMonth] = useState("");
  const [party, setParty] = useState("");
  const [group, setGroup] = useState("");
  const [groups, setGroups] = useState([]);
  const [partyNames, setPartyNames] = useState([]);

  const company = user?.company_name || "";

  const buildParams = useCallback((d) => {
    const p = { party, group };
    if (viewMode === "year") p.year = "1";
    else if (viewMode === "month") p.month = month || new Date().toISOString().slice(0, 7);
    else if (d) p.date = d;
    return p;
  }, [viewMode, month, party, group]);

  const load = useCallback(async (d) => {
    try {
      const res = await axios.get(`${API}/day-book`, { params: buildParams(d) });
      setDate(res.data.date);
      setRows(res.data.rows || []);
      setTotals(res.data.grand_total || { debit: 0, credit: 0 });
      setSummary(res.data.summary || null);
      setRange({ from: res.data.from, to: res.data.to, mode: res.data.mode });
      setSelected(0);
    } catch {}
  }, [buildParams]);

  const dfv = useDateFilterVersion();
  useEffect(() => {
    const df = getDateFilter();
    if (df && df.from !== df.to && viewMode !== "year") { setViewMode("year"); return; }
    load(df && df.from === df.to ? df.from : undefined);
  }, [load, dfv]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    (async () => {
      try {
        const [g, ps, pp] = await Promise.all([
          axios.get(`${API}/account-groups`),
          axios.get(`${API}/voucher-parties`, { params: { vch_type: "receipt" } }),
          axios.get(`${API}/voucher-parties`, { params: { vch_type: "payment" } }),
        ]);
        setGroups(g.data.groups || []);
        const names = new Set([
          ...(ps.data.parties || []).map((p) => p.name),
          ...(pp.data.parties || []).map((p) => p.name),
        ]);
        setPartyNames([...names].sort());
      } catch {}
    })();
  }, []);

  const download = async (format) => {
    try {
      const res = await axios.get(`${API}/day-book/export`, {
        params: { ...buildParams(date), format }, responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = format === "pdf" ? "daybook.pdf" : "daybook.xlsx"; a.click();
      toast.success(`${format === "pdf" ? "PDF" : "Excel"} downloaded`);
    } catch { toast.error("Download failed"); }
  };

  const openEntry = useCallback((r) => {
    if (!r || !r.src) return;
    if (r.src === "sales" && r.id) navigate(`/invoices/${r.id}/edit?return=daybook`);
    else if (r.src === "purchase" && r.id) navigate(`/purchases?edit=${r.id}&return=daybook`);
    else if (r.src === "voucher" && r.id) navigate(`/vouchers?edit=${r.id}&type=${r.vtype || "receipt"}&return=daybook`);
    else if (r.src === "bank") navigate("/bank?return=daybook");
  }, [navigate]);

  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target.tagName || "").toLowerCase();
      if (e.key === "Escape") {
        e.preventDefault(); e.stopPropagation();
        if (showFilter) setShowFilter(false); else onClose();
        return;
      }
      if (tag === "input" || tag === "select") return;
      if (e.key === "ArrowDown") { e.preventDefault(); setSelected((s) => Math.min(s + 1, rows.length - 1)); }
      else if (e.key === "ArrowUp") { e.preventDefault(); setSelected((s) => Math.max(s - 1, 0)); }
      else if (e.key === "Enter") { e.preventDefault(); openEntry(rows[selected]); }
      else if (e.key === "ArrowLeft" && viewMode === "day") { e.preventDefault(); if (date) load(shiftDay(date, -1)); }
      else if (e.key === "ArrowRight" && viewMode === "day") { e.preventDefault(); if (date) load(shiftDay(date, 1)); }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose, rows, selected, date, load, viewMode, showFilter, openEntry]);

  const periodLabel = range.mode === "day"
    ? `For ${date ? fmtDate(date) : ""}`
    : `${fmtDate(range.from)} to ${fmtDate(range.to)}`;

  return (
    <div className="w-full" data-testid="day-book-view">
      <div className="w-full overflow-hidden shadow-xl flex flex-col" style={{ background: "#fbf7ee", border: "1px solid #e7dcc3", height: "calc(100vh - 0.5rem)" }}>
      {/* Title bar */}
      <div className="flex items-center justify-between px-3 py-1.5 text-white" style={{ background: "#0f766e" }}>
        <span className="text-sm font-bold" data-testid="db-title">Day Book</span>
        <span className="text-sm font-black uppercase tracking-wide" data-testid="db-company-title">{company}</span>
        <div className="flex items-center gap-1 pr-12">
          <button onClick={() => download("xlsx")} data-testid="db-download-excel" title="Download Excel"
            className="p-1 hover:bg-white/20 rounded-sm"><FileXls size={16} weight="bold" /></button>
          <button onClick={() => download("pdf")} data-testid="db-download-pdf" title="Download PDF"
            className="p-1 hover:bg-white/20 rounded-sm"><FilePdf size={16} weight="bold" /></button>
          <button onClick={() => setShowFilter((v) => !v)} data-testid="db-filter-btn" title="Filters"
            className={`p-1 rounded-sm ${showFilter || party || group || viewMode !== "day" ? "bg-white/25" : "hover:bg-white/20"}`}>
            <Funnel size={16} weight="bold" /></button>
          <button onClick={onClose} data-testid="db-close-btn" className="p-1 hover:bg-white/20 rounded-sm">
            <X size={16} weight="bold" />
          </button>
        </div>
      </div>

      {/* Filter panel */}
      {showFilter && (
        <div className="px-4 py-2 border-b border-slate-300 bg-[#f5efdd] flex flex-wrap items-end gap-3" data-testid="db-filter-panel">
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-0.5">View</label>
            <select value={viewMode} onChange={(e) => setViewMode(e.target.value)} data-testid="db-filter-view"
              className="px-2 py-1 border border-slate-300 rounded-sm text-xs bg-white">
              <option value="day">Day wise</option>
              <option value="month">Month wise</option>
              <option value="year">Full Year (FY)</option>
            </select>
          </div>
          {viewMode === "month" && (
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-0.5">Month</label>
              <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="db-filter-month"
                className="px-2 py-1 border border-slate-300 rounded-sm text-xs bg-white" />
            </div>
          )}
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-0.5">Party / Company</label>
            <input list="db-party-list" value={party} onChange={(e) => setParty(e.target.value)}
              placeholder="All parties" data-testid="db-filter-party"
              className="px-2 py-1 border border-slate-300 rounded-sm text-xs bg-white w-52" />
            <datalist id="db-party-list">
              {partyNames.map((n) => <option key={n} value={n} />)}
            </datalist>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-0.5">Under Group</label>
            <select value={group} onChange={(e) => setGroup(e.target.value)} data-testid="db-filter-group"
              className="px-2 py-1 border border-slate-300 rounded-sm text-xs bg-white w-48">
              <option value="">All groups</option>
              {groups.map((g) => <option key={g.id} value={g.name}>{g.name}</option>)}
            </select>
          </div>
          <button onClick={() => { setParty(""); setGroup(""); setViewMode("day"); setMonth(""); }}
            data-testid="db-filter-clear"
            className="px-3 py-1 text-xs font-bold text-slate-700 border border-slate-400 rounded-sm hover:bg-white">
            Clear
          </button>
        </div>
      )}

      {/* Sub header */}
      <div className="flex items-center justify-between px-4 py-1">
        <span className="text-sm font-bold text-slate-800">Day Book</span>
        <div className="flex items-center gap-1">
          {viewMode === "day" && (
            <button onClick={() => date && load(shiftDay(date, -1))} data-testid="db-prev-day"
              className="p-1 text-slate-700 hover:bg-[#f1e9d6] rounded-sm"><CaretLeft size={14} weight="bold" /></button>
          )}
          <span className="text-sm font-bold text-slate-900" data-testid="db-date">{periodLabel}</span>
          {viewMode === "day" && (
            <>
              <button onClick={() => date && load(shiftDay(date, 1))} data-testid="db-next-day"
                className="p-1 text-slate-700 hover:bg-[#f1e9d6] rounded-sm"><CaretRight size={14} weight="bold" /></button>
              <input type="date" value={date} onChange={(e) => e.target.value && load(e.target.value)}
                data-testid="db-date-input"
                className="ml-2 px-1 py-0.5 text-xs border border-slate-300 rounded-sm bg-white" />
            </>
          )}
        </div>
      </div>

      {/* Summary strip */}
      {summary && (
        <div className="flex flex-wrap gap-x-5 gap-y-0.5 px-4 pb-1 text-[11px] font-semibold text-slate-700" data-testid="db-summary">
          <span>Sales: <b>₹{fmtINR(summary.sales) || "0"}</b> ({summary.n_sales})</span>
          <span>Purchase: <b>₹{fmtINR(summary.purchase) || "0"}</b> ({summary.n_purchase})</span>
          <span className="text-emerald-700">Received: <b>₹{fmtINR(summary.received) || "0"}</b> ({summary.n_received})</span>
          <span className="text-red-700">Paid: <b>₹{fmtINR(summary.paid) || "0"}</b> ({summary.n_paid})</span>
        </div>
      )}

      {/* Columns header */}
      <div className="flex items-end border-t border-b-2 border-slate-700 text-xs font-bold text-slate-800">
        <div className="w-[100px] px-3 py-1 text-center">Date</div>
        <div className="flex-1 px-2 py-1">Particulars</div>
        <div className="w-[95px] px-2 py-1 text-center">Vch Type</div>
        <div className="w-[100px] px-2 py-1 text-center">Vch No.</div>
        <div className="w-[105px] px-2 py-1 text-right">CGST</div>
        <div className="w-[105px] px-2 py-1 text-right">SGST</div>
        <div className="w-[105px] px-2 py-1 text-right">IGST</div>
        <div className="w-[130px] px-2 py-1 text-right">Debit Amount</div>
        <div className="w-[130px] px-2 py-1 text-right">Credit Amount</div>
      </div>

      {/* Rows */}
      <div className="flex-1 overflow-y-auto" data-testid="db-rows">
        {rows.map((r, idx) => (
          <div key={idx} onMouseEnter={() => setSelected(idx)} onClick={() => openEntry(r)}
            className={`flex items-center cursor-pointer text-sm ${selected === idx ? "bg-teal-600 text-white" : "text-slate-900 hover:bg-[#f1e9d6]"}`}
            data-testid={`db-row-${idx}`}>
            <div className="w-[100px] px-3 py-0.5 text-center">{fmtDate(r.date)}</div>
            <div className="flex-1 px-2 py-0.5 font-bold">{r.name}</div>
            <div className="w-[95px] px-2 py-0.5 text-center font-semibold">{r.vch_type}</div>
            <div className="w-[100px] px-2 py-0.5 text-center">{r.vch_no}</div>
            <div className="w-[105px] px-2 py-0.5 text-right">{fmtINR(r.cgst)}</div>
            <div className="w-[105px] px-2 py-0.5 text-right">{fmtINR(r.sgst)}</div>
            <div className="w-[105px] px-2 py-0.5 text-right">{fmtINR(r.igst)}</div>
            <div className="w-[130px] px-2 py-0.5 text-right font-semibold">{fmtINR(r.debit)}</div>
            <div className="w-[130px] px-2 py-0.5 text-right font-semibold">{fmtINR(r.credit)}</div>
          </div>
        ))}
        {rows.length === 0 && (
          <div className="px-4 py-6 text-sm text-slate-500 text-center" data-testid="db-empty">No entries found</div>
        )}
      </div>

      {/* Grand Total */}
      <div className="flex items-center border-t-2 border-slate-700 bg-[#f1e9d6] text-sm font-black text-slate-900" data-testid="db-grand-total">
        <div className="flex-1 px-4 py-1.5 tracking-[0.25em]">Grand Total</div>
        <div className="w-[105px] px-2 py-1.5 text-right" data-testid="db-grand-cgst">{fmtINR(rows.reduce((s, r) => s + (r.cgst || 0), 0))}</div>
        <div className="w-[105px] px-2 py-1.5 text-right" data-testid="db-grand-sgst">{fmtINR(rows.reduce((s, r) => s + (r.sgst || 0), 0))}</div>
        <div className="w-[105px] px-2 py-1.5 text-right" data-testid="db-grand-igst">{fmtINR(rows.reduce((s, r) => s + (r.igst || 0), 0))}</div>
        <div className="w-[130px] px-2 py-1.5 text-right" data-testid="db-grand-debit">{fmtINR(totals.debit)}</div>
        <div className="w-[130px] px-2 py-1.5 text-right" data-testid="db-grand-credit">{fmtINR(totals.credit)}</div>
      </div>
      </div>
    </div>
  );
}
